import { Button } from '@/components/ui/button'
import FilePreview from '@/components/molecules/file-preview'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useState } from 'react'
import DynamicFieldGenerator from './dynamic-field-generator'
import { useChatContext } from '@/context/ChatProvider'
import { useSocketContext } from '@/context/SocketProvider'
import { FileText, XCircle } from 'lucide-react'
import { usePersistStore } from '@/store/persist.store'
import { useChatStore, type DynamicFormContent, type DynamicFormField } from '@/store/chat.store'
import { useSocketStore } from '@/store/socket.store'
import { useTranslations } from 'next-intl'
import { resolveFormConfigText } from '@/lib/form-config-translations'
import { addOptimisticFormSubmission, clearFormRetryState } from '@/lib/form-message-state'
import { v4 as uuidv4 } from 'uuid'

interface DynamicFormGeneratorProps {
  data: DynamicFormContent
  onSubmit?: (formData: Record<string, any>) => void
}

type SubmittedFileItem = {
  id?: string
  name?: string
  blobUrl?: string
  blobType?: string
  url?: string
}

const DynamicFormGenerator = ({ data }: DynamicFormGeneratorProps) => {
  const t = useTranslations('dynamicForm')
  const token = usePersistStore(state => state.token)
  const siteId = usePersistStore(state => state.siteId)

  const setMessages = useChatStore(state => state.setMessages)
  const { scrollToBottom } = useChatContext()
  const { sendFrame, canSend } = useSocketContext()

  const socketConnectionStatus = useSocketStore(state => state.socketConnectionStatus)
  const isMessageResponseWaiting = useSocketStore(state => state.isMessageResponseWaiting)
  const setIsMessageResponseWaiting = useSocketStore(state => state.setIsMessageResponseWaiting)
  const isReconnecting = useSocketStore(state => state.isReconnecting)
  const setPendingUploadFileAck = useSocketStore(state => state.setPendingUploadFileAck)
  const resolveText = (value: string | undefined) => resolveFormConfigText(value, t)
  const fields = (Array.isArray(data?.fields) ? data.fields : []).map(field => ({
    ...field,
    placeholder: resolveText(field.placeholder) ?? field.placeholder
  }))
  const title = resolveText(data?.title) ?? data?.title
  const introductionMessage = resolveText(data?.introduction_message) ?? data?.introduction_message
  const isSubmitted = Boolean(data?.submitted)
  const isAnswered = Boolean(data?.answered)
  const submittedFields = data?.submitted_fields ?? {}
  const retryFields = data?.pending_submission_fields ?? {}
  const retryDisplayFields = data?.pending_submission_display_fields ?? retryFields
  const submitButtonText =
    data?.submit_button_text && !['Gönder', 'Gonder'].includes(data.submit_button_text)
      ? (resolveText(data.submit_button_text) ?? data.submit_button_text)
      : t('submit')

  // Dynamic file handling states - organized by field name
  const [fileStates, setFileStates] = useState<
    Record<
      string,
      {
        files: File[]
        previewUrls: string[]
        uploadedResponses: any[]
      }
    >
  >({})

  const UPLOAD_FILE_ACK_TIMEOUT_MS = 15000

  const getRetainedFileIds = (value: unknown): string[] => {
    const values = Array.isArray(value) ? value : value ? [value] : []

    return values
      .map(item => {
        if (typeof item === 'string') return item
        if (!item || typeof item !== 'object') return null

        const file = item as Record<string, any>

        return file.id ?? file.fileId ?? file.file_id ?? file.currentFileId
      })
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  }

  const waitForUploadFileAck = (): Promise<void> =>
    Promise.race([
      new Promise<void>((resolve, reject) => {
        setPendingUploadFileAck({ resolve, reject })
      }),
      new Promise<void>((_, reject) =>
        setTimeout(() => {
          setPendingUploadFileAck(null)
          reject(new Error('The file upload timed out.'))
        }, UPLOAD_FILE_ACK_TIMEOUT_MS)
      )
    ]).finally(() => setPendingUploadFileAck(null))

  // File upload: API upload, then send fileId via WS and wait for ack. No blob download.
  const handleMultipleFileUpload = async (files: File[]) => {
    setIsMessageResponseWaiting(true)
    const results: {
      file: File
      blobUrl: string
      blobType: string
      fileId: string
      data: any
    }[] = []

    try {
      for (const file of files) {
        const formData = new FormData()
        if (siteId) formData.append('site_id', siteId)
        formData.append('file', file)

        try {
          const uploadRes = await fetch('/api/upload', {
            method: 'POST',
            body: formData,
            headers: { Authorization: `Bearer ${token}` }
          })

          if (!uploadRes.ok) {
            throw new Error(`Upload error: ${uploadRes.status}`)
          }

          const uploadData = await uploadRes.json()
          const fileId =
            uploadData?.data?.file_id ?? uploadData?.data?.data?.file_id ?? uploadData?.id ?? uploadData?.data?.id

          if (!fileId) {
            throw new Error('file_id yok')
          }

          if (canSend()) {
            await sendFrame(JSON.stringify({ type: 'upload_file', content: fileId }))
            await waitForUploadFileAck()
          }

          const blobType = file.type || 'application/octet-stream'
          const blobUrl = URL.createObjectURL(file)

          results.push({
            file,
            blobUrl,
            blobType,
            fileId,
            data: uploadData
          })
        } catch (err) {
          console.error('Upload/upload_file ack error:', err)
        }
      }
    } finally {
      // Release the composer lock once the upload flow settles. This flag is
      // otherwise only cleared by a later WS frame; releasing it here avoids
      // leaving the composer disabled if no such frame arrives.
      setIsMessageResponseWaiting(false)
    }

    return results
  }
  // Dynamic Yup schema generation based on fields
  const generateValidationSchema = (fields: DynamicFormField[]) => {
    const schema: Record<string, any> = {}

    fields.forEach(field => {
      let fieldSchema

      switch (field.type) {
        case 'email':
          fieldSchema = Yup.string().email('Enter a valid email address.')
          break
        case 'number':
          fieldSchema = Yup.number().typeError('Enter a valid number.')
          break
        case 'date':
          fieldSchema = Yup.date().typeError('Enter a valid date.')
          break
        case 'tel':
          fieldSchema = Yup.string().matches(/^[\+]?[0-9\s\-\(\)]+$/, 'Enter a valid phone number.')
          break
        case 'file':
          // nullable() so the null initial value reaches the custom test below
          // instead of tripping Yup's default "cannot be null" message
          fieldSchema = Yup.mixed().nullable().test(
            'file-required',
            `${field.placeholder || field.name} is required.`,
            function () {
              if (!field.required) return true
              const fieldFiles = fileStates[field.name]?.files || []
              const retainedFileIds = getRetainedFileIds(retryFields[field.name])

              return fieldFiles.length > 0 || retainedFileIds.length > 0
            }
          )
          break
        case 'select':
          fieldSchema = Yup.string().oneOf(
            ['', ...(field.options || []).map(option => option.value)],
            'Select a valid option.'
          )
          break
        default:
          fieldSchema = Yup.string()
      }

      // Add required validation if field is required (except for file fields)
      if (field.required && field.type !== 'file') {
        fieldSchema = fieldSchema.required(`${field.placeholder || field.name} is required.`)
      }

      schema[field.name] = fieldSchema
    })

    return Yup.object().shape(schema)
  }

  // Generate initial values
  const generateInitialValues = (fields: DynamicFormField[], sourceFields: Record<string, unknown> = retryFields) => {
    const values: Record<string, any> = {}
    fields.forEach(field => {
      switch (field.type) {
        case 'file':
          values[field.name] = null // File fields start as null
          break
        default:
          values[field.name] = sourceFields[field.name] ?? ''
      }
    })

    return values
  }

  const validationSchema = fields.length > 0 ? generateValidationSchema(fields) : Yup.object()
  const initialValues = fields.length > 0 ? generateInitialValues(fields) : {}

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: values => {
      sendFormMessage(values)
    }
  })

  const handleClearForm = () => {
    formik.resetForm({ values: generateInitialValues(fields, {}) })
    setFileStates({})

    if (
      !data?.form_id ||
      (!data.submission_error && !data.pending_submission_fields && !data.pending_submission_display_fields)
    ) {
      return
    }

    setMessages(prev => clearFormRetryState(prev, data.form_id as string) as typeof prev)
  }

  const handleFileChange = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const fileFiles = files.filter(file => file.type.startsWith('image/') || file.type === 'application/pdf')

    if (fileFiles.length > 0) {
      setFileStates(prev => {
        const currentState = prev[fieldName] || {
          files: [],
          previewUrls: [],
          uploadedResponses: []
        }

        return {
          ...prev,
          [fieldName]: {
            ...currentState,
            files: [...currentState.files, ...fileFiles],
            previewUrls: [...currentState.previewUrls], // Will be updated below
            uploadedResponses: [...currentState.uploadedResponses]
          }
        }
      })

      const readers = fileFiles.map(file => {
        return new Promise<string>(resolve => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })
      })

      Promise.all(readers).then(newUrls => {
        setFileStates(prev => ({
          ...prev,
          [fieldName]: {
            ...prev[fieldName],
            previewUrls: [...(prev[fieldName]?.previewUrls || []), ...newUrls]
          }
        }))
      })

      // Set formik value for file field
      formik.setFieldValue(fieldName, fileFiles)
    }

    event.target.value = ''
  }

  const sendFormMessage = async (formValues: Record<string, any>) => {
    if (isSubmitted || isAnswered || fields.length === 0) return

    if (socketConnectionStatus !== 'opened' || !canSend()) {
      const errorMessage = isReconnecting
        ? 'Reconnecting to the server. Please wait...'
        : 'The server connection is not currently available.'

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: errorMessage,
          type: 'error'
        }
      ])
      scrollToBottom()

      return
    }

    if (isMessageResponseWaiting) return

    // Handle file uploads for all file fields
    const fileFieldNames = fields.filter(field => field.type === 'file').map(field => field.name) || []
    const uploadedFileData: Record<string, any[]> = {}
    const submittedFileData: Record<string, SubmittedFileItem[]> = {}

    for (const fieldName of fileFieldNames) {
      const fieldFiles = fileStates[fieldName]?.files || []
      if (fieldFiles.length > 0) {
        const uploadedResponses = await handleMultipleFileUpload(fieldFiles)
        uploadedFileData[fieldName] = uploadedResponses
        submittedFileData[fieldName] = uploadedResponses
          .map(r => ({
            id: r?.fileId,
            name: r?.file?.name,
            blobUrl: r?.blobUrl,
            blobType: r?.blobType
          }))
          .filter(file => !!file.id || !!file.blobUrl)

        // Update fileStates with uploaded responses
        setFileStates(prev => ({
          ...prev,
          [fieldName]: {
            ...prev[fieldName],
            uploadedResponses: uploadedResponses
          }
        }))
      }
    }

    // Build fields: file fields get IDs from uploadedFileData, others from formValues
    const fieldsForContent: Record<string, unknown> = {}
    const fieldsForDisplay: Record<string, unknown> = {}
    fields.forEach(field => {
      if (field.type === 'file') {
        const ids = uploadedFileData[field.name]?.map(r => r?.fileId).filter(Boolean) ?? []
        const retainedIds = getRetainedFileIds(retryFields[field.name])
        fieldsForContent[field.name] = ids.length > 0 ? ids : retainedIds
        fieldsForDisplay[field.name] = submittedFileData[field.name] ?? retryDisplayFields[field.name] ?? retainedIds
      } else {
        fieldsForContent[field.name] = formValues[field.name]
        fieldsForDisplay[field.name] = formValues[field.name]
      }
    })

    // Create JSON string content with form submission type (file_upload etc. have ID arrays inside)
    const submittedFormId = data?.form_id || 'unknown'
    const answerId = data?.answer_id || uuidv4()
    const messageContent = JSON.stringify({
      type: 'form_submission',
      form_id: submittedFormId,
      answer_id: answerId,
      fields: fieldsForContent
    })

    setIsMessageResponseWaiting(true)

    const outgoing = {
      content: messageContent,
      type: 'message'
    }

    sendFrame(JSON.stringify(outgoing))

    setMessages(
      prev =>
        addOptimisticFormSubmission(prev, {
          formId: submittedFormId,
          answerId,
          formConfig: {
            ...data,
            fields
          },
          fields: fieldsForContent,
          displayFields: fieldsForDisplay
        }) as typeof prev
    )
    scrollToBottom()

    // Clear form and files
    setFileStates({})
    formik.resetForm()

    // Release the composer lock once the form submission is dispatched. The
    // form is optimistically marked answered, so the submit controls are
    // disabled independently of this flag; not waiting for a later WS frame
    // avoids leaving the composer disabled if no such frame arrives.
    setIsMessageResponseWaiting(false)
  }

  const getSubmittedFileItems = (value: unknown): SubmittedFileItem[] => {
    const values = Array.isArray(value) ? value : value ? [value] : []

    return values
      .map(item => {
        if (typeof item === 'string') return { id: item }
        if (!item || typeof item !== 'object') return null

        const file = item as Record<string, any>
        const id = file.id ?? file.fileId ?? file.file_id ?? file.currentFileId

        return {
          id,
          name: file.name ?? file.filename ?? file.file_name,
          blobUrl: file.blobUrl ?? file.preview_url ?? file.previewUrl,
          blobType: file.blobType ?? file.mime_type ?? file.content_type ?? file.type,
          url: file.url ?? file.download_url ?? file.downloadUrl
        }
      })
      .filter(Boolean) as SubmittedFileItem[]
  }

  const formatSubmittedValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '-'
    if (Array.isArray(value)) return value.map(item => String(item)).join(', ') || '-'
    if (typeof value === 'object') return JSON.stringify(value)

    return String(value)
  }

  const renderFileValue = (field: DynamicFormField, value: unknown) => {
    const files = getSubmittedFileItems(value)
    if (files.length === 0) {
      return <div className='text-muted-foreground bg-background/70 rounded-lg border px-3 py-2 text-xs'>-</div>
    }

    return (
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
        {files.map((file, index) => {
          const href = file.url ?? file.blobUrl ?? (file.id ? `${window.location.origin}/api/download/${file.id}` : undefined)
          const label = file.name ?? file.id ?? `${field.placeholder || field.name} ${index + 1}`
          const isImage =
            !!file.blobUrl &&
            (file.blobType?.startsWith('image/') || (!file.blobType && !file.blobUrl.includes('application/pdf')))

          const content = (
            <>
              <span className='bg-muted flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border'>
                {isImage ? (
                  <img src={file.blobUrl} alt={label} className='size-full object-cover' />
                ) : (
                  <span className='text-muted-foreground flex flex-col items-center gap-1'>
                    <FileText size={22} />
                    <span className='max-w-full truncate px-1 text-[10px]'>
                      {file.blobType?.includes('pdf') ? 'PDF' : 'File'}
                    </span>
                  </span>
                )}
              </span>
              <span className='text-muted-foreground min-w-0 truncate text-[11px]'>{label}</span>
            </>
          )

          if (!href) {
            return (
              <div key={`${field.name}-${index}`} className='flex min-w-0 flex-col gap-1'>
                {content}
              </div>
            )
          }

          return (
            <FilePreview
              key={`${field.name}-${index}`}
              fileId={file.id}
              blobUrl={file.blobUrl}
              blobType={file.blobType}
              url={href}
              name={label}
              className='hover:bg-muted/70 flex min-w-0 flex-col gap-1 rounded-lg p-1 transition-colors'
            >
              {content}
            </FilePreview>
          )
        })}
      </div>
    )
  }

  const renderSubmittedForm = () => (
    <div className='mt-2 flex flex-col gap-3 rounded-xl border border-dashed p-2'>
      <div className='text-primary flex items-center justify-end text-xs font-semibold' aria-live='polite'>
        {data?.pending_submission === true ? t('submitting') : t('submitted')}
      </div>

      {fields.map(field => (
        <div key={field.name} className='flex flex-col gap-1'>
          <div className='text-muted-foreground text-xs font-medium'>{field.placeholder || field.name}</div>
          {field.type === 'file' ? (
            renderSubmittedFileValue(field)
          ) : (
            <div className='bg-background/70 rounded-lg border px-3 py-2 text-xs leading-normal break-words'>
              {field.type === 'select'
                ? field.options?.find(option => option.value === submittedFields[field.name])?.label ||
                  formatSubmittedValue(submittedFields[field.name])
                : formatSubmittedValue(submittedFields[field.name])}
            </div>
          )}
        </div>
      ))}
    </div>
  )

  const renderSubmittedFileValue = (field: DynamicFormField) => renderFileValue(field, submittedFields[field.name])

  const renderRetainedFileValue = (field: DynamicFormField) => {
    const value = retryDisplayFields[field.name]
    if (!value || getSubmittedFileItems(value).length === 0) return null

    return (
      <div className='space-y-1'>
        <p className='text-muted-foreground text-xs font-medium'>{t('retainedFiles')}</p>
        {renderFileValue(field, value)}
      </div>
    )
  }

  if (data?.submitted_fields_redacted === true || fields.length === 0) return null

  return (
    <div className='text-sm leading-7 font-medium'>
      {title && <p className='text-primary font-bold'>{title}</p>}
      {introductionMessage && <p className='mt-2 text-xs leading-normal italic'>{introductionMessage}</p>}
      {data?.submission_error && (
        <div className='mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs leading-normal text-red-700 dark:border-red-700 dark:bg-red-950/60 dark:text-red-200'>
          {data.submission_error === 'processing_pending_after_persistence'
            ? t('processingPendingAfterPersistence')
            : t('submitError')}
        </div>
      )}

      {isSubmitted ? (
        renderSubmittedForm()
      ) : (
        <form
          id={data?.form_id}
          onSubmit={formik.handleSubmit}
          className='mt-2 flex flex-col gap-4 rounded-xl border border-dashed p-2'
        >
          {fields.map(field => (
            <div key={field.name} className='space-y-2'>
              <DynamicFieldGenerator
                field={field}
                formik={formik}
                onFileChange={handleFileChange}
                disabled={isAnswered}
              />
              {field.type === 'file' && renderRetainedFileValue(field)}
            </div>
          ))}

          {/* Dynamic File Preview Areas */}
          {Object.entries(fileStates).map(([fieldName, state]) => {
            if (state.previewUrls.length === 0) return null

            const field = fields.find(f => f.name === fieldName)
            if (!field) return null

            return (
              <div key={fieldName} className='space-y-2'>
                <p className='text-xs font-medium text-gray-600 dark:text-gray-400'>
                  {field.placeholder} ({state.files.length} {state.files.length === 1 ? 'file' : 'files'})
                </p>
                <div className='relative grid grid-cols-4 items-center gap-2 rounded-xl border bg-gray-100 p-2 sm:grid-cols-6 dark:bg-gray-700'>
                  {state.previewUrls.map((url, i) => (
                    <div key={`${fieldName}-${i}`} className='relative overflow-hidden rounded-xl border'>
                      {url.includes('application/pdf') ? (
                        <div className='flex aspect-square size-full flex-col items-center justify-center gap-1 bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'>
                          <FileText size={24} />
                          <b className='text-xs'>PDF</b>
                        </div>
                      ) : (
                        <img
                          src={url}
                          alt={`Preview ${fieldName} ${i}`}
                          className='aspect-square size-full object-cover'
                        />
                      )}
                      <Button
                        disabled={isMessageResponseWaiting || isAnswered}
                        variant='ghost'
                        size='icon'
                        onClick={() => {
                          setFileStates(prev => ({
                            ...prev,
                            [fieldName]: {
                              ...prev[fieldName],
                              files: prev[fieldName].files.filter((_, index) => index !== i),
                              previewUrls: prev[fieldName].previewUrls.filter((_, index) => index !== i),
                              uploadedResponses: prev[fieldName].uploadedResponses.filter((_, index) => index !== i)
                            }
                          }))

                          // Update formik value
                          const remainingFiles = fileStates[fieldName].files.filter((_, index) => index !== i)
                          formik.setFieldValue(fieldName, remainingFiles.length > 0 ? remainingFiles : null)
                        }}
                        className='absolute top-1 right-1 h-6 w-6 text-gray-500 hover:text-red-500'
                      >
                        <XCircle size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          <div className='flex items-center justify-end gap-1'>
            <Button type='button' variant='ghost' onClick={handleClearForm} disabled={isAnswered}>
              {t('clear')}
            </Button>

            <Button type='submit' disabled={isMessageResponseWaiting || isAnswered}>
              {isMessageResponseWaiting ? t('submitting') : submitButtonText}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

export default DynamicFormGenerator
